import { Dayjs } from 'dayjs'
import useNote from '../../hooks/useNote'
import {
  SelectedDate,
  selectedDateToDay,
  useSelectedDate,
} from '../../contexts/SelectedDate'
import { useSelectedRecordName } from '../../contexts/SelectedRecordName'
import { useSidebarProvider } from '../../contexts/Sidebar'
import { NoteType, isCalendarNote, isTeamspaceNote } from '../../utils/note'
import { useBreadcrumbPath } from '../../hooks/useBreadcrumb'
import { findSidebarEntry } from '../sidebar/findSidebarEntry'
import classNames from 'classnames'

function generateCalendarNoteTitleAndDetail(
  date: Dayjs, // Replace 'any' with the appropriate type for 'date'
  selectedDate: SelectedDate
): { title: string; detail: string } {
  let title = ''
  let detail = ''

  switch (selectedDate.activePeriod) {
    case 'week': {
      title = `Week ${selectedDate.week.toString()}, ${selectedDate.year.toString()}`
      const startOfWeek = date.startOf('week')
      const endOfWeek = date.endOf('week')
      detail = startOfWeek.format('MMM D') + ' - ' + endOfWeek.format('MMM D')
      break
    }
    case 'month': {
      const isCurrentYear = date.year() === new Date().getFullYear()
      title = date.format(isCurrentYear ? 'MMMM' : 'MMMM YYYY')
      break
    }
    case 'quarter': {
      title = `Q${date.quarter().toString()}, ${date.year().toString()}`
      const startOfQuarter = date.startOf('quarter')
      detail = [0, 1, 2]
        .map((m) => startOfQuarter.add(m, 'month').format('MMM'))
        .join(' - ')
      break
    }
    case 'year': {
      title = date.format('YYYY')
      break
    }
    default: {
      const format = 'ddd, D MMM'
      title = date.calendar(undefined, {
        lastDay: `[Yesterday], ${format}`,
        sameDay: `[Today], ${format}`,
        nextDay: `[Tomorrow], ${format}`,
        nextWeek: format,
        lastWeek: format,
        sameElse: format,
      })
    }
  }

  return { title, detail }
}

function parsePageTitle(
  selectedDate: SelectedDate,
  isLoading: boolean,
  isError: boolean,
  noteTitle = '',
  noteType: NoteType = NoteType.CALENDAR_NOTE,
  filename: string | null | undefined,
  breadcrumbPath: string,
  error?: unknown,
  note?: ReturnType<typeof useNote>['data']
): { title: string; folder?: string; detail?: string } {
  const date = selectedDateToDay(selectedDate)
  let title = 'NotePlan for Web'
  let detail = ''
  let folder: string | undefined = undefined

  if (isLoading) {
    return {
      title: 'Loading ...',
      folder: noteType === NoteType.PROJECT_NOTE ? 'Loading ...' : undefined,
    }
  }

  if (isError) {
    const message: string | undefined =
      error && typeof error === 'object' && 'message' in error
        ? (error.message as string)
        : error?.toString()
    return { title: message ?? 'Error' }
  }

  if (noteTitle && noteTitle.length > 0) {
    title = noteTitle
  }

  if (isCalendarNote(noteType)) {
    const result = generateCalendarNoteTitleAndDetail(date, selectedDate)
    title = result.title
    detail = result.detail
  } else {
    const pathComponents = filename?.split('/')
    folder =
      pathComponents && pathComponents.length > 1
        ? // Case CloudKit
          // Take all components except the last
          pathComponents
            .slice(0, -1)
            .map((component) =>
              component.startsWith('@') ? component.slice(1) : component
            )
            .join('/')
        : // Case Supabase
          breadcrumbPath

    // For root-level notes, show "Notes" as the folder
    if (!folder || folder.length === 0) {
      folder = 'Notes'
    }
  }

  return { title, folder, detail }
}

function determineIcon(
  selectedDate: SelectedDate,
  folder: string | undefined,
  noteType: NoteType | undefined
): string {
  if (folder) {
    if (folder.includes('Templates')) {
      return 'far fa-clipboard'
    }
    // Check if it's a teamspace folder
    if (noteType && isTeamspaceNote(noteType)) {
      return 'far fa-screen-users text-green-500'
    }
    return 'far fa-folder'
  }
  if (noteType && isTeamspaceNote(noteType) && !isCalendarNote(noteType)) {
    return 'far fa-screen-users text-green-500'
  }

  switch (selectedDate.activePeriod) {
    case 'week': {
      return 'far fa-calendar-week'
    }
    case 'month': {
      return 'far fa-calendar-range'
    }
    case 'quarter': {
      return 'far fa-calendar-days'
    }
    case 'year': {
      return 'far fa-calendar-days'
    }
    default: {
      return 'far fa-calendar-star'
    }
  }
}

function determineIconColor(selectedDate: SelectedDate): string {
  switch (selectedDate.activePeriod) {
    case 'week': {
      return 'text-fuchsia-600 dark:text-fuchsia-500'
    }
    case 'month': {
      return 'text-red-700 dark:text-red-600'
    }
    case 'quarter': {
      return 'text-orange-700 dark:text-orange-600'
    }
    case 'year': {
      return 'text-yellow-600 dark:text-yellow-500'
    }
    default: {
      return 'text-orange-600 dark:text-orange-400'
    }
  }
}

type Props = {
  onToday: (_isWeek: boolean) => void
}

export const NoteTitle = ({ onToday }: Props) => {
  const selectedDate = useSelectedDate()
  const selectedRecordName = useSelectedRecordName()
  const { noteKey, handleRevealNote } = useSidebarProvider()
  const { isLoading, isError, error, data: note } = useNote(noteKey)

  // Get breadcrumb path for Supabase notes
  const breadcrumbPath = useBreadcrumbPath(selectedRecordName)

  const { title, folder, detail } = parsePageTitle(
    selectedDate,
    isLoading,
    isError,
    note?.title,
    note?.noteType ?? NoteType.CALENDAR_NOTE,
    note?.filename,
    breadcrumbPath,
    error,
    note
  )

  const icon = determineIcon(selectedDate, folder, note?.noteType)
  const iconColor = determineIconColor(selectedDate)

  return (
    <button
      className='flex w-full items-center justify-center px-6 py-0 md:items-center md:justify-start md:p-0'
      type='button'
      data-tooltip-id='my-tooltip'
      data-tooltip-content={folder ? 'Show note in sidebar' : 'Open today (⌃T)'}
      onClick={() => {
        folder ? handleRevealNote(selectedRecordName) : onToday(false)
      }}
    >
      <i
        className={`${icon} ${folder ? 'text-blue-500' : iconColor} mr-1.5 hidden text-xl md:inline`}
      ></i>
      <div className='flex items-end'>
        <h1 className='font-regular text-base md:text-lg md:font-bold'>
          {folder ?? title}
        </h1>
        <h2
          className={classNames(
            'mb-0.5 ml-2 text-xs text-gray-400 md:mb-1',
            detail ? '' : 'hidden'
          )}
        >
          {detail}
        </h2>
      </div>
    </button>
  )
}
